import { useEffect, useRef, useState } from "react";

/**
 * useActivityStream — subscribe to /api/ws/activity for live activity events.
 * Returns: { connected, items, push } where items is the latest event list (newest first).
 */
export default function useActivityStream(initial = []) {
  const [items, setItems] = useState(initial);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    setItems(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sentinel_token");
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    if (!token || !backend) return undefined;
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/activity?token=${encodeURIComponent(token)}`;

    let cancelled = false;
    let pingTimer = null;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      let ws;
      try { ws = new WebSocket(wsUrl); } catch (_) { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true); retryRef.current = 0;
        pingTimer = setInterval(() => { try { ws.send("ping"); } catch (_) {} }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const j = JSON.parse(ev.data);
          if (j.type === "activity" && j.item) {
            setItems((prev) => [j.item, ...prev].slice(0, 30));
          }
        } catch (_) { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) clearInterval(pingTimer);
        if (cancelled) return;
        retryRef.current = Math.min(retryRef.current + 1, 6);
        retryTimer = setTimeout(connect, Math.min(2000 * retryRef.current, 10000));
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    };
    connect();

    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch (_) {}
    };
  }, []);

  const seed = (list) => setItems(list);
  return { connected, items, seed };
}
